import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";
import {useStore} from "react-redux";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import SearchIcon from "@mui/icons-material/Search";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import type {RootState} from "../../store/store";
import {fetchTenants, tenantsSelectors, updateTenantInCache} from "../../store/tenantsSlice";
import type {GlobalFeatureFlags, TenantSummaryResponse, TenantUpdateRequest} from "../../types/tenant";
import {getTenantById, updateTenant} from "../../api/tenantDetailApi";
import {BOOLEAN_FEATURE_FLAGS} from "../../constants/featureFlags";
import {featureFlagLabel} from "../../constants/featureFlagLabels";
import {effectiveFlag, nextMatrixValue, storedFlagValue} from "../../utils/featureFlagResolution";
import {colors, fonts} from "../../theme/tokens";
import {SectionCard} from "../ui/SectionCard";
import type {TernaryFlagValue} from "./TernaryFeatureFlag";

const DEBOUNCE_MS = 500;

type TenantFeatureFlagsTableProps = {
    /** Global defaults, for the effective value of inherited cells; `null` until loaded. */
    globalFlags: GlobalFeatureFlags | null;
    onError: (message: string) => void;
    onSaved: (message: string) => void;
};

const cellKey = (tenantId: string, flag: string) => `${tenantId}::${flag}`;

export function TenantFeatureFlagsTable({globalFlags, onError, onSaved}: TenantFeatureFlagsTableProps) {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const tenantsStatus = useAppSelector((s) => s.tenants.status);

    const [search, setSearch] = useState("");
    // Optimistic values of cells changed in this session; the store holds the last saved state.
    const [localValues, setLocalValues] = useState<Record<string, TernaryFlagValue>>({});
    const [pendingKeys, setPendingKeys] = useState<Set<string>>(new Set());
    const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    useEffect(() => {
        if (tenantsStatus === "idle") void dispatch(fetchTenants());
    }, [dispatch, tenantsStatus]);

    useEffect(() => {
        const timers = debounceTimers.current;
        return () => Object.values(timers).forEach(clearTimeout);
    }, []);

    const filteredTenants = useMemo(() => {
        const query = search.trim().toLowerCase();
        return query ? tenants.filter((tenant) => tenant.name.toLowerCase().includes(query)) : tenants;
    }, [tenants, search]);

    const setPending = (key: string, pending: boolean) =>
            setPendingKeys((current) => {
                const next = new Set(current);
                if (pending) next.add(key);
                else next.delete(key);
                return next;
            });

    const displayValue = (tenant: TenantSummaryResponse, flag: string): TernaryFlagValue => {
        const key = cellKey(tenant.id, flag);
        return key in localValues ? localValues[key] : storedFlagValue(tenant.featureConfig ?? {}, flag);
    };

    const stateLabel = useCallback(
            (value: TernaryFlagValue) => (value === true ? t("FLAGS_ON") : value === false ? t("FLAGS_OFF") : t("FLAGS_INHERIT")),
            [t],
    );

    const persist = useCallback(
            async (tenantId: string, flag: string, value: TernaryFlagValue) => {
                const key = cellKey(tenantId, flag);
                const saved = tenantsSelectors.selectById(store.getState(), tenantId);
                const previous = storedFlagValue(saved?.featureConfig ?? {}, flag);
                try {
                    // Name, notes, engineers and blocklist come fresh from the server; the flags come from the stored
                    // summary, because the detail endpoint resolves inheritance and writing it back would freeze it.
                    const full = await getTenantById(tenantId);
                    const latest = tenantsSelectors.selectById(store.getState(), tenantId);
                    const featureConfig = {...(latest?.featureConfig ?? {}), [flag]: value};
                    const request: TenantUpdateRequest = {
                        name: full.name,
                        notes: full.notes,
                        featureConfig,
                        assignedSupportEngineerIds: full.assignedSupportEngineerIds ?? [],
                        blockList: full.blockList,
                    };
                    await updateTenant(tenantId, request);
                    dispatch(updateTenantInCache({id: tenantId, changes: {featureConfig}}));
                    setLocalValues((current) => {
                        const next = {...current};
                        delete next[key];
                        return next;
                    });
                    onSaved(t("FLAGS_SAVED_TOAST", {tenant: full.name, flag: featureFlagLabel(t, flag), state: stateLabel(value)}));
                } catch (e) {
                    onError(e instanceof Error ? e.message : t("FLAGS_UPDATE_TENANT_ERROR"));
                    setLocalValues((current) => ({...current, [key]: previous}));
                } finally {
                    setPending(key, false);
                }
            },
            [dispatch, onError, onSaved, stateLabel, store, t],
    );

    const cycle = (tenant: TenantSummaryResponse, flag: string) => {
        const key = cellKey(tenant.id, flag);
        const next = nextMatrixValue(displayValue(tenant, flag));
        setLocalValues((current) => ({...current, [key]: next}));
        setPending(key, true);
        clearTimeout(debounceTimers.current[key]);
        debounceTimers.current[key] = setTimeout(() => {
            delete debounceTimers.current[key];
            void persist(tenant.id, flag, next);
        }, DEBOUNCE_MS);
    };

    const loading = tenantsStatus === "loading" && tenants.length === 0;

    return (
            <SectionCard
                    flush
                    title={t("FLAGS_TENANT_OVERRIDES")}
                    helper={t("FLAGS_AUTOSAVE_HELPER")}
                    actions={
                        <>
                            <TextField
                                    size="small"
                                    placeholder={t("FLAGS_SEARCH_TENANTS_PLACEHOLDER")}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    sx={{width: 260}}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon sx={{fontSize: 18, color: colors.faint}} />
                                                    </InputAdornment>
                                            ),
                                        },
                                        htmlInput: {"aria-label": t("FLAGS_SEARCH_TENANTS_PLACEHOLDER")},
                                    }}
                            />
                            <Legend />
                        </>
                    }
            >
                <TableContainer sx={{maxHeight: "70vh"}}>
                    <Table stickyHeader size="small" aria-label={t("FLAGS_TABLE_ARIA")} sx={{"& td:not(:first-of-type):not(:last-of-type), & th:not(:first-of-type):not(:last-of-type)": {px: 0.5}, "& td:first-of-type, & th:first-of-type": {pl: 2}, "& td:last-of-type, & th:last-of-type": {pr: 2}}}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{verticalAlign: "bottom", minWidth: 220, pb: 1.25}}>{t("TENANTS_COLUMN_NAME")}</TableCell>
                                {BOOLEAN_FEATURE_FLAGS.map((flag) => (
                                        <TableCell key={flag} align="center" sx={{height: 140, verticalAlign: "bottom", pb: 1.25, width: 44}}>
                                            <Box
                                                    component="span"
                                                    sx={{
                                                        writingMode: "vertical-rl",
                                                        transform: "rotate(180deg)",
                                                        textTransform: "none",
                                                        letterSpacing: 0,
                                                        fontSize: "11.5px",
                                                        fontWeight: 500,
                                                        whiteSpace: "nowrap",
                                                        display: "inline-block",
                                                    }}
                                            >
                                                {featureFlagLabel(t, flag)}
                                            </Box>
                                        </TableCell>
                                ))}
                                <TableCell sx={{verticalAlign: "bottom", pb: 1.25}}>{t("FLAGS_SMS_VIA")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading &&
                                    Array.from({length: 6}, (_, row) => (
                                            <TableRow key={row}>
                                                <TableCell colSpan={BOOLEAN_FEATURE_FLAGS.length + 2}>
                                                    <Skeleton height={24} />
                                                </TableCell>
                                            </TableRow>
                                    ))}
                            {filteredTenants.map((tenant) => (
                                    <TableRow key={tenant.id} hover>
                                        <TableCell sx={{whiteSpace: "nowrap"}}>
                                            <Box component={Link} to={`/tenants/${tenant.id}`} sx={{color: colors.ink, fontWeight: 500, textDecoration: "none", "&:hover": {color: colors.primary, textDecoration: "underline"}}}>
                                                {tenant.name}
                                            </Box>
                                        </TableCell>
                                        {BOOLEAN_FEATURE_FLAGS.map((flag) => {
                                            const key = cellKey(tenant.id, flag);
                                            const value = displayValue(tenant, flag);
                                            const effective = effectiveFlag(value, globalFlags?.[flag]);
                                            return (
                                                    <TableCell key={flag} align="center">
                                                        <MatrixCell
                                                                value={value}
                                                                inheritedLabel={effective.source === "global" ? (effective.value ? t("FLAGS_ON_SHORT_LOWER") : t("FLAGS_OFF_SHORT_LOWER")) : "·"}
                                                                pending={pendingKeys.has(key)}
                                                                label={t("FLAGS_CELL_ARIA", {
                                                                    tenant: tenant.name,
                                                                    flag: featureFlagLabel(t, flag),
                                                                    state: value === null
                                                                            ? effective.source === "global"
                                                                                    ? t("FLAGS_INHERIT_EFFECTIVE", {state: stateLabel(effective.value)})
                                                                                    : t("FLAGS_INHERIT")
                                                                            : stateLabel(value),
                                                                    next: stateLabel(nextMatrixValue(value)),
                                                                })}
                                                                onClick={() => cycle(tenant, flag)}
                                                        />
                                                    </TableCell>
                                            );
                                        })}
                                        <TableCell sx={{fontFamily: fonts.mono, fontSize: "11.5px", color: colors.muted, whiteSpace: "nowrap"}}>
                                            {tenant.featureConfig?.smsProvider === "IBASIS" ? "IBASIS" : tenant.featureConfig?.smsProvider === "AWS_SNS" ? "AWS SNS" : "-"}
                                        </TableCell>
                                    </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <Typography variant="body2" sx={{px: 2, py: 1.25, color: colors.muted, borderTop: `1px solid ${colors.sectionDivider}`}}>
                    {t("FLAGS_MATRIX_FOOTER", {shown: filteredTenants.length, total: tenants.length})}
                </Typography>
            </SectionCard>
    );
}

function MatrixCell({value, inheritedLabel, pending, label, onClick}: {
    value: TernaryFlagValue;
    inheritedLabel: string;
    pending: boolean;
    label: string;
    onClick: () => void;
}) {
    const {t} = useTranslation();
    const palette =
            value === true
                    ? {color: colors.success, background: colors.successTint, border: "#bfe0cc", text: t("FLAGS_TERNARY_ON_SHORT")}
                    : value === false
                            ? {color: colors.error, background: colors.errorTint, border: colors.errorPanelBorder, text: t("FLAGS_TERNARY_OFF_SHORT")}
                            : {color: colors.placeholder, background: colors.tableHeaderAlt, border: colors.rowDivider, text: inheritedLabel};
    return (
            <ButtonBase
                    onClick={onClick}
                    aria-label={label}
                    title={label}
                    aria-busy={pending}
                    sx={{
                        width: 36,
                        height: 22,
                        borderRadius: "3px",
                        fontSize: "10px",
                        fontWeight: value === null ? 400 : 600,
                        color: palette.color,
                        backgroundColor: palette.background,
                        border: `1px solid ${palette.border}`,
                        opacity: pending ? 0.55 : 1,
                        backgroundImage: pending ? `linear-gradient(90deg, transparent 0 40%, rgba(31,90,168,.18) 50%, transparent 60% 100%)` : "none",
                        backgroundSize: "200% 100%",
                        animation: pending ? "matrixPending 1s linear infinite" : "none",
                        "@keyframes matrixPending": {from: {backgroundPosition: "100% 0"}, to: {backgroundPosition: "-100% 0"}},
                        "&.Mui-focusVisible": {outline: `2px solid ${colors.primary}`, outlineOffset: 1},
                    }}
            >
                {palette.text}
            </ButtonBase>
    );
}

function Legend() {
    const {t} = useTranslation();
    const swatch = (background: string, border: string) => (
            <Box component="span" aria-hidden sx={{display: "inline-block", width: 18, height: 12, borderRadius: "2px", backgroundColor: background, border: `1px solid ${border}`, mr: 0.75, verticalAlign: "middle"}} />
    );
    return (
            <Box sx={{display: "flex", gap: 1.5, fontSize: "12px", color: colors.muted, flexWrap: "wrap"}}>
                <span>{swatch(colors.successTint, "#bfe0cc")}{t("FLAGS_LEGEND_FORCED_ON")}</span>
                <span>{swatch(colors.errorTint, colors.errorPanelBorder)}{t("FLAGS_LEGEND_FORCED_OFF")}</span>
                <span>{swatch(colors.tableHeaderAlt, colors.rowDivider)}{t("FLAGS_LEGEND_INHERITS")}</span>
            </Box>
    );
}
